<template>
  <div>
    <FunnelBreadcrumb :funnel-hash="funnelHash" :has-shipping="false" />
    <div class="mx-auto my-0 max-w-[34rem] p-32">
      <div class="mb-32 border-b py-16 text-center">
        <span class="body-1">
          {{ i18n(translations.headerTitle) }}
        </span>
      </div>
      <h1 class="heading-1 mb-16">
        {{ i18n(translations.punchline) }}
      </h1>
      <p class="body-1 mb-16" data-qa="offer" data-test="offer">
        <FormattedMessage :definition="translations.offer">
          <template #amount>
            <span class="body-1-bold">
              {{ data?.price_with_currency }}
            </span>
          </template>
          <template #model>
            <span class="body-1-bold">
              {{ data?.title }}
            </span>
          </template>
        </FormattedMessage>
      </p>
      <div
        class="mx-auto my-0 flex hidden max-w-[34rem] text-right md:mx-0 md:mb-24 md:mt-16 md:block"
      >
        <RevButton data-qa="decline" variant="secondary" @click="declineOffer">
          {{ i18n(translations.decline) }}
        </RevButton>
        <RevButton
          class="ml-16"
          data-qa="accept"
          variant="primary"
          @click="acceptOffer"
        >
          {{ i18n(translations.accept) }}
        </RevButton>
      </div>
      <div
        class="border-static-default-mid rounded-md mb-24 overflow-hidden border px-20"
      >
        <RevList>
          <RevListItemStatic
            v-for="(diagnostic, index) in data?.diagnostic_summary"
            :key="`${index}-${diagnostic.value}`"
          >
            <template #label>
              <p class="body-1">
                {{ diagnostic.label }}
              </p>
            </template>
            <template #suffix>
              <p class="body-1">
                {{ diagnostic.value }}
              </p>
            </template>
          </RevListItemStatic>
        </RevList>
      </div>
      <p class="body-1-bold mb-16">
        {{ i18n(translations.nextStep) }}
      </p>

      <div
        v-for="wording in data?.wording"
        :key="wording.title"
        class="mb-16 flex"
      >
        <div>
          <IconCheckInCircle />
        </div>
        <div class="ml-16">
          <p class="body-1">
            {{ wording.title }}
          </p>

          <p
            v-for="paragraph in wording.paragraphs"
            :key="paragraph"
            class="body-2"
          >
            {{ paragraph }}
          </p>
        </div>
      </div>

      <span class="my-32 block h-1 w-full" />
      <p class="body-1-bold mb-16">
        {{ i18n(translations.lastThing) }}
      </p>
      <p class="body-1">
        {{ i18n(translations.lastThingParagraph) }}
      </p>
      <div
        class="mx-auto my-0 flex hidden max-w-[34rem] text-right md:mx-0 md:mb-24 md:mt-16 md:block"
      >
        <RevButton data-qa="decline" variant="secondary" @click="declineOffer">
          {{ i18n(translations.decline) }}
        </RevButton>
        <RevButton
          class="ml-16"
          data-qa="accept"
          variant="primary"
          @click="acceptOffer"
        >
          {{ i18n(translations.accept) }}
        </RevButton>
      </div>
    </div>
    <RevStickyBar class="md:hidden">
      <div
        class="mx-auto my-0 flex max-w-[34rem] text-right md:mx-0 md:mb-24 md:mt-16"
      >
        <RevButton
          data-qa="decline"
          full-width="always"
          variant="secondary"
          @click="declineOffer"
        >
          {{ i18n(translations.decline) }}
        </RevButton>
        <RevButton
          class="ml-16"
          data-qa="accept"
          full-width="always"
          variant="primary"
          @click="acceptOffer"
        >
          {{ i18n(translations.accept) }}
        </RevButton>
      </div>
    </RevStickyBar>
  </div>
</template>

<script lang="ts" setup>
import { createError, useRouteParams, useRouter } from '#imports'
import { computed } from 'vue'

import { buybackCustomerAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'

import { HOME } from '~/scopes/home/route-names'

import FunnelBreadcrumb from '../components/FunnelBreadcrumb/FunnelBreadcrumb.vue'
import { ROUTE_NAMES } from '../constants'

import translations from './Offer.translations'

const i18n = useI18n()
const router = useRouter()
const routeParams = useRouteParams()
const { trackClick } = useTracking()

withDefaults(
  defineProps<{
    funnelHash?: Record<string, string>
  }>(),
  {
    funnelHash: () => ({}),
  },
)

const { data, error, status } = await useHttpFetch(
  buybackCustomerAPI.getOffer,
  {
    pathParams: { sourcingListingId: routeParams?.offerId },
  },
)

// OFFER EXPIRED
if (data.value === null && status.value === 'success') {
  router.push({ name: HOME })
}

if (error.value) {
  // fatal: true option redirects to Error view
  throw createError({
    statusCode: error.value.statusCode,
    statusMessage: error.value.message,
    fatal: true,
  })
}

const nextStep = computed(() =>
  data.value?.hasBankDetails
    ? {
        name: ROUTE_NAMES.BANK,
        params: routeParams,
      }
    : {
        name: ROUTE_NAMES.ADDRESS,
        params: routeParams,
      },
)

const trackAction = (name: string) => {
  trackClick({
    zone: 'buyback',
    name,
    value: {
      ...data.value?.diagnostic_payload,
      estimation: data.value?.price,
    },
  })
}

function acceptOffer() {
  trackAction('accept_offer')
  router.push(nextStep.value)
}

function declineOffer() {
  trackAction('decline_offer')
  router.push({
    name: HOME,
    params: {
      locale: useI18nLocale(),
    },
  })
}
</script>
